import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/utils/cn';

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> & {
  label?: string;
  labelAdornment?: ReactNode;
  required?: boolean;
  error?: string;
  hint?: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, labelAdornment, required, error, hint, prefix, suffix, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="inline-flex items-center gap-1 text-sm font-medium text-ink-soft">
          {required && (
            <span aria-hidden="true" className="mr-0.5 text-danger">
              *
            </span>
          )}
          {label}
          {labelAdornment}
        </label>
      )}
      <div
        className={cn(
          'flex items-center gap-2 rounded-xl border bg-white px-3 transition-colors',
          error ? 'border-red-400 focus-within:border-red-500' : 'border-slate-300 focus-within:border-brand-500',
          'shadow-sm focus-within:ring-2',
          error ? 'focus-within:ring-red-200' : 'focus-within:ring-brand-200',
        )}
      >
        {prefix && <span className="text-ink-muted">{prefix}</span>}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            'min-w-0 flex-1 bg-transparent py-3 text-base text-ink outline-none placeholder:text-ink-faint',
            'disabled:cursor-not-allowed disabled:opacity-60',
            className,
          )}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...rest}
        />
        {suffix && <span className="text-ink-muted">{suffix}</span>}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="text-xs font-medium text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-ink-muted">{hint}</p>
      ) : null}
    </div>
  );
});
